import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Result } from 'antd';

import EventFormElement from '../components/events/EventFormElement';
import PublicFileField from '../components/common/PublicFileField';
import TurnstileWidget, { effectiveSiteKey } from '../components/common/TurnstileWidget';
import ReportPageLink from '../components/common/ReportPageLink';
import { LOCK } from '../components/icons';
import { RecordIdentity, RecordTitle } from '../components/common/RecordBadge';
import { WithoutCardDefinitions } from '../contexts/CardDefinitionsContext';
import { getSharedView, removeSharedUpload, submitSharedForm, uploadSharedFile } from '../services/shareLinks';
import { formatDisplayDate } from '../utils/dateDisplay';
import { getElementInitialValue, getElementValidationErrors, isElementValueEmpty } from '../utils/elements';
import {
	describeSubmitError, describeUploadError, isFileElement, precheckPublicFile, publicFileKind, uploadRetryAfter,
} from '../utils/intakeLinks';
import { isLimitReached } from '../utils/publicForms';
import { planLimitOf } from '../utils/planLimit';
import PlanLimitAlert from '../components/common/PlanLimitAlert';
import { FORM_NOTICE_HEADING, FORM_NOTICE_PRIVATE, FORM_NOTICE_PUBLISHED } from '../config/formNotice';
import { collectionZone } from '../utils/collectionTimezone';
import {
	carryValues, changedQuestions, CHANGE_WORDS, clearDraft, formShape, readDraft, writeDraft,
} from '../utils/formChanges';
import { isRepeatingGroup } from '../config/repeatingGroups';
import {
	ENTRY_COLUMN, entriesForWire, entriesOf, entryErrorKey, entryKeyFromRefusal, plainRefusal, reconcileGroupErrors,
	validateGroupEntries, visibleColumns,
} from '../utils/repeatingGroupEntries';

/** The card's own name for one record ("Inspection"), whichever shape `label` has. */
const cardTitleOf = card => (typeof card?.label === 'string' && card.label.trim())
	|| card?.label?.singular
	|| card?.name
	|| 'Form';

// An upload refused for the FILE itself: sending it again cannot help.
const FINAL_UPLOAD_CODES = ['file_wrong_kind', 'file_too_large', 'upload_unreadable', 'upload_limit', 'upload_allowance_reached'];
// How many times a busy server (503 upload_busy) is asked again, and the
// longest wait between (its Retry-After, capped).
const BUSY_RETRIES = 3;
const sleep = ms => new Promise(resolve => { setTimeout(resolve, ms); });
const previewOf = (file, kind) => (kind === 'photo' && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
	? URL.createObjectURL(file) : null);
const dropPreview = (entry) => {
	if (entry?.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(entry.previewUrl);
};
let uidSeq = 0;

// How long an upload waits for the bot check's next token before it gives
// up and says so (a public form's uploads each need a fresh one, KPR-512).
const CHALLENGE_WAIT_MS = 60000;

/**
 * The form's way to the API, for an INVITE LINK: its share code. A permanent
 * public form (KPR-513, pages/PublicFormPage) hands the page its own — the
 * same five calls at `/api/forms/{collectionSlug}/{formSlug}` — so the page
 * itself never knows which kind it is serving beyond what the descriptor and
 * these say. `isLive(view)`: is a freshly fetched descriptor still a form
 * this page can serve (an intake descriptor here; anything else is the link
 * dying under the visitor).
 */
export const shareCodeFormApi = code => ({
	refresh: () => getSharedView(code),
	isLive: fresh => fresh?.kind === 'intake',
	submit: payload => submitSharedForm(code, payload),
	upload: ({ file, element, submissionToken }, options) => uploadSharedFile(code, { file, element, submissionToken }, options),
	remove: (id, token) => removeSharedUpload(code, id, token),
});

/**
 * A READ-ONLY element (KPR-519): one the form shows and the visitor cannot
 * change — an element rule lets the public see it, not edit it (the Salem
 * form's "Status: New"). The API serves it with `?shown=1`, `readOnly: true`
 * and the `value` the record will hold. It is never validated here and never
 * sent: the submit writes its value itself.
 */
export const isReadOnlyElement = def => def?.readOnly === true;

/** The served value of every read-only element over `values` — what it shows is what the record gets, whatever a draft or a reload carried. */
const withShownValues = (elements, values) => {
	const out = { ...values };
	(elements || []).forEach((def) => {
		if (!def?.name || !isReadOnlyElement(def)) return;
		if (def.value === null || def.value === undefined) delete out[def.name];
		else out[def.name] = def.value;
	});
	return out;
};

// `view.timezone` is the collection's zone, present when a shown element
// starts at today, now or a relative date (the owner's ruling B11): the form
// opens it at the day the submit's create would store, never the visitor's.
// A read-only element opens at the value the API served (withShownValues).
const initialValuesFor = (elements, timezone) => {
	const zone = collectionZone(timezone);
	const values = {};
	(elements || []).forEach((def) => {
		if (!def?.name || isReadOnlyElement(def)) return;
		const initial = getElementInitialValue(def, { isNewRecord: true, zone });
		if (initial !== undefined) values[def.name] = initial;
	});
	return withShownValues(elements, values);
};

/** The elements the visitor answers: every served one but the read-only ones. */
const answerableOf = elements => (elements || []).filter(def => !isReadOnlyElement(def));

/**
 * Every message the form's own check has for `values`: each field's (the item
 * form's check), and each repeating group's — its fewest entries and each
 * entry's columns, filed under `<group>` and `<group>.<entry>.<column>`
 * (KPR-689, utils/repeatingGroupEntries, the item form's own).
 */
function checkAnswers(elements, values) {
	const answerable = answerableOf(elements);
	const found = getElementValidationErrors(answerable, values);
	answerable.filter(isRepeatingGroup).forEach((def) => {
		delete found[def.name];
		Object.assign(found, validateGroupEntries(def, entriesOf(values?.[def.name])));
	});
	return found;
}

/**
 * Where a refusal of the submit lands when it is about a repeating group
 * (KPR-689): the API names the group, and for each entry it refused its
 * `position` (as sent) and `column`. Every refusal in `errors` is placed on
 * its entry and column (the entry itself when the column is not one the
 * visitor sees); the group's own sentence (too few or too many entries) on
 * the group. → the error map to merge, or null when the refusal is not one.
 */
export function groupRefusalErrors(data, group, list) {
	if (!group || !data || data.element !== group.name) return null;
	const out = {};
	const refusals = Array.isArray(data.errors) && data.errors.length ? data.errors : [data];
	const columns = new Set(visibleColumns(group).map(column => column.name));
	let entries = 0;
	refusals.forEach((refusal) => {
		if (!refusal || (refusal.element && refusal.element !== group.name)) return;
		// The API says where first ("history": entry 2, column "years": …);
		// the entry and the column are where the sentence is shown.
		const text = plainRefusal(String(refusal.message || '').replace(/^"[^"]*":\s*/, ''));
		const key = Number.isInteger(refusal.position) ? entryKeyFromRefusal(list, refusal) : null;
		if (!key) {
			if (!out[group.name]) out[group.name] = text || 'Check these entries.';
			return;
		}
		const column = refusal.column && columns.has(refusal.column) ? refusal.column : ENTRY_COLUMN;
		const at = entryErrorKey(group.name, key, column);
		if (!out[at]) { out[at] = text || 'Check this answer.'; entries += 1; }
	});
	if (entries && !out[group.name]) out[group.name] = entries === 1 ? '1 entry needs attention.' : `${entries} entries need attention.`;
	return out;
}

/**
 * What the form opens with (KPR-515): its own starting values, and — for a
 * permanent public form with a draft in this tab (`draftKey`) of the same
 * card — the answers the visitor typed before, put back by element name
 * (utils/formChanges carryValues: still asked, the same type, never a file),
 * with the questions that changed since marked. ⚑ Only a draft of the SAME
 * card: an address re-pointed to another card's form shares names by
 * accident, not by meaning.
 */
function startOf(view, draftKey) {
	const values = initialValuesFor(view?.elements, view?.timezone);
	const draft = draftKey ? readDraft(draftKey) : null;
	if (!draft || (draft.cardId && draft.cardId !== `${view?.card?._id || ''}`)) {
		return { values, marks: {}, restored: false };
	}
	const { carried, dropped } = carryValues(view?.elements, draft.values, draft.shape);
	const merged = withShownValues(view?.elements, { ...values, ...carried });
	return {
		values: merged,
		marks: changedQuestions(view?.elements, draft.shape, merged, dropped),
		// A draft kept before KPR-792 may hold `notes`: never read (public
		// forms no longer offer a notes box).
		restored: Object.keys(carried).length > 0,
		dropped: dropped.length > 0,
	};
}

/**
 * THE PUBLIC INTAKE FORM — what a visitor holding an `intake` share link sees
 * at `/s/:code`, with no account and no session. ShortLinkPage resolves the
 * code and hands this page the descriptor when its `kind` is 'intake'.
 *
 * ⚠ THIS PAGE MAKES NO AUTHENTICATED CALL. Everything it renders comes from
 * the one public descriptor (`GET /api/share/{code}`), and the only requests
 * of its own are the submit and — to get a fresh single-use token — that same
 * public descriptor again. Three things keep it that way:
 *
 *   - The route sits outside the app shell (PublicShell), so no rail, no
 *     palette and none of their warming fetches mount around it.
 *   - The subtree renders under `WithoutCardDefinitions`: the element inputs
 *     read the card cache OPTIONALLY, and with no provider in reach they read
 *     nothing and fetch nothing.
 *   - The elements that WOULD reach for the collection — a person field, a
 *     record lookup, a calculated or server-numbered value — can never be on
 *     an intake link's allowlist; the API refuses them at mint time and again
 *     on every fetch, so this page never has to hope.
 *
 * The inputs are the item form's own (`EventFormElement`), and the check
 * before Send is the item form's own (`getElementValidationErrors`, over the
 * vendored `required` rule the API runs), so what a visitor is told here is
 * what the server would say.
 *
 * READ-ONLY ELEMENTS (KPR-519). The page asks with `?shown=1`, and an
 * element an element rule lets the public SEE but not change arrives with
 * `readOnly: true` and the `value` the record will hold: it is rendered
 * disabled at that value with one line saying why, never validated here and
 * never sent — the submit writes it as the server's value.
 *
 * PHOTOS (KPR-388). A `file` element is never the item form's uploader
 * (that one needs a session): it is `PublicFileField`, and THIS page owns its
 * files. Each picked file is checked here first (kind, size, the form's
 * caps), then sent at once — raw bytes to the link's upload route under the
 * form's submission token, with no credentials of whoever is signed in on
 * this machine — and its id becomes the element's value at Send. A file is
 * previewed from the visitor's own device; nothing comes back from keepr.
 * An upload is bound to the token it went under, so when the token is
 * refreshed (it aged out) every file on the form is sent again under the
 * new one. A busy server is asked again a few times; Remove takes a file
 * back at once.
 */
export default function SharedFormPage({
	code,
	view: initialView,
	onDead,
	onOutOfDate,
	// KPR-513: the calls this form makes (shareCodeFormApi above for an
	// invite link, the default), and what the page does when a fresh fetch
	// finds the form paused or its window full (a permanent public form only;
	// without these, either reads as the form dying).
	formApi = null,
	onPaused = null,
	onLimit = null,
	// A permanent public form proves a person on EVERY upload (KPR-512 review
	// A1): each file waits for a fresh single-use bot-check token, taken from
	// the one widget the Send button also uses.
	uploadNeedsChallenge = false,
	// KPR-514: what the foot of the page offers to report it. An invite link
	// reports by its code (the default below); a permanent public form passes
	// its own "Report this form", which needs a bot check of its own.
	reportLink = null,
	// KPR-515 (f09): a permanent public form keeps what the visitor types in
	// THEIR OWN TAB's sessionStorage under this key (utils/formChanges), so a
	// reload — after the owner fixes a form that stopped — brings it back.
	// Never sent, never in a URL, never a file; cleared by a successful send.
	// An invite link passes none: the plan changes nothing about them.
	draftKey = null,
}) {
	const calls = useMemo(() => formApi || shareCodeFormApi(code), [formApi, code]);
	const [view, setView] = useState(initialView);
	const elements = useMemo(
		() => (Array.isArray(view?.elements) ? view.elements.filter(def => def?.name) : []),
		[view]
	);
	// Read once, on the first render: what the form opens with.
	const [start] = useState(() => startOf(initialView, draftKey));
	const [values, setValues] = useState(start.values);
	// The newest answers, for the handlers and the reload (KPR-515, KPR-689).
	const valuesRef = useRef(values);
	valuesRef.current = values;
	const [errors, setErrors] = useState({});
	const [planLimit, setPlanLimit] = useState(null);
	const [formError, setFormError] = useState(null);
	const [notice, setNotice] = useState(() => (start.restored
		? `Your answers from earlier are back.${Object.keys(start.marks).length ? ' The form has changed since — look at the highlighted questions.' : ''}`
		: null));
	// A restored draft can be thrown away (a shared tablet: the answers may
	// not be this visitor's) — Start over, beside the notice.
	const [restoredDraft, setRestoredDraft] = useState(start.restored);
	// KPR-515: the questions to point at after the form changed under the
	// visitor (utils/formChanges changedQuestions): name → 'new' | 'required'
	// | 'retyped'. `focusFirst` asks the effect below to scroll there once.
	const [marks, setMarks] = useState(start.marks);
	const [focusFirst, setFocusFirst] = useState(() => Object.keys(start.marks).length > 0);
	// Nothing is kept until the visitor has typed something (a restored
	// draft counts): an untouched form leaves no trace in the tab.
	const dirtyRef = useRef(start.restored);
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(null);
	const [turnstileToken, setTurnstileToken] = useState(null);
	const [turnstileReset, setTurnstileReset] = useState(0);
	// The visitor's files, by element: [{ uid, element, file, name, size,
	// kind, previewUrl, state: 'uploading'|'done'|'error', percent, error,
	// id, token, retryable }]. A ref mirrors it for the async senders.
	const [files, setFiles] = useState({});
	const filesRef = useRef(files);
	const [fileErrors, setFileErrors] = useState({});
	const viewRef = useRef(view);
	viewRef.current = view;
	// Set on EVERY mount, not only at creation: React's StrictMode mounts,
	// unmounts and mounts again in development, and a ref left false by the
	// first cleanup made every finished upload look abandoned (it never read
	// Ready) — found on the live check.
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			Object.values(filesRef.current).flat().forEach(dropPreview);
		};
	}, []);
	// One send in flight at a time. The button's spinner stops a second
	// click, not a second Enter; and a second request carrying the same
	// token is a REPLAY, whose answer is `allowAnother: false` — landing
	// after the first it would take "Submit another" off the screen.
	const inFlight = useRef(false);

	const card = view?.card || null;
	const collection = view?.collection || null;
	const cardTitle = cardTitleOf(card);
	const expires = view?.expiresAt ? formatDisplayDate(view.expiresAt) : null;
	// KPR-389: the questions asked PRIVATELY come after the others, in their
	// own section — their answers go to the people who run the collection and
	// are never part of the record. `published`: a global collection, whose
	// accepted records every account reads; the note says so before a word
	// is typed.
	const publicElements = useMemo(() => elements.filter(def => def.private !== true), [elements]);
	const privateElements = useMemo(() => elements.filter(def => def.private === true), [elements]);
	const published = collection?.published === true;

	// `botCheck` is null when the deployment runs without one. When it is set,
	// its siteKey is the API's own; null there means "the build's key".
	const siteKey = view?.botCheck ? effectiveSiteKey(view.botCheck.siteKey || null) : '';
	const needsChallenge = Boolean(view?.botCheck);
	const siteKeyRef = useRef(siteKey);
	siteKeyRef.current = siteKey;

	const handleChange = useCallback((name, value) => {
		dirtyRef.current = true;
		const previous = valuesRef.current?.[name];
		setValues(prev => ({ ...prev, [name]: value }));
		// A repeating group (KPR-689) keeps a message only while the cell it
		// names is still wrong — the item form's own rule.
		const group = (viewRef.current?.elements || []).find(def => def?.name === name && isRepeatingGroup(def));
		if (group) {
			setErrors(prev => reconcileGroupErrors(prev, group, entriesOf(value), { previous: entriesOf(previous) }));
			return;
		}
		setErrors(prev => (prev[name] ? { ...prev, [name]: null } : prev));
		// ⚠ The mark STAYS while the visitor answers: taking it off would swap
		// the framed field for a bare one under the same key, and React would
		// remount the input after the first character — focus lost, a phone's
		// keyboard closed (the independent review's M1). It goes with the next
		// reload or the send.
	}, []);
	// The draft (KPR-515): every change the visitor makes, kept in their tab.
	useEffect(() => {
		if (!draftKey || !dirtyRef.current) return;
		writeDraft(draftKey, { cardId: `${view?.card?._id || ''}`, elements, values });
	}, [draftKey, view, elements, values]);

	// Scroll to and focus the first question that changed, once per reload.
	useEffect(() => {
		if (!focusFirst) return;
		setFocusFirst(false);
		const first = elements.find(def => marks[def.name]);
		if (!first || typeof document === 'undefined') return;
		const box = Array.from(document.querySelectorAll('[data-form-change]')).find(node => node.getAttribute('data-form-change') === first.name);
		if (!box) return;
		if (typeof box.scrollIntoView === 'function') box.scrollIntoView({ block: 'center' });
		const input = box.querySelector('input:not([type="hidden"]), textarea, select, button, [tabindex]:not([tabindex="-1"])');
		if (input && typeof input.focus === 'function') input.focus({ preventScroll: true });
	}, [focusFirst, elements, marks]);

	// A fresh descriptor, for a fresh single-use token: after a send, for
	// "Submit another", and when the old token aged out. A 404 here is the
	// link dying under the visitor (the cap reached, revoked, expired).
	const refreshToken = useCallback(async () => {
		try {
			const fresh = await calls.refresh();
			// A permanent form paused while the visitor was here (KPR-513).
			if (fresh?.paused === true && onPaused) { onPaused(fresh); return null; }
			if (!calls.isLive(fresh)) { onDead?.(); return null; }
			setView(fresh);
			return fresh;
		} catch (error) {
			if (error?.response?.status === 400 && error?.response?.data?.code === 'form_out_of_date') onOutOfDate?.();
			else if (isLimitReached(error) && onLimit) onLimit(error);
			else onDead?.();
			return null;
		}
	}, [calls, onDead, onOutOfDate, onPaused, onLimit]);

	// ---- the bot check's tokens ----
	//
	// One widget, one token at a time, each good for ONE request. The Send
	// button reads `turnstileToken`. When uploads need a token of their own
	// (`uploadNeedsChallenge`), a file takes the token in hand — the widget
	// is reset at once for the next — or waits in line for the next one the
	// widget issues. Rejected: a second widget for the uploads, which would
	// put two "verifying you are human" boxes on one form, and reusing one
	// token, which the API refuses (a Turnstile token is single use).
	const tokenRef = useRef(null);
	const waitersRef = useRef([]);
	const handleWidgetToken = useCallback((token) => {
		if (token && waitersRef.current.length) {
			const waiter = waitersRef.current.shift();
			waiter.resolve(token);
			setTurnstileReset(n => n + 1);
			return;
		}
		tokenRef.current = token || null;
		setTurnstileToken(token || null);
	}, []);
	const takeChallengeToken = useCallback(() => {
		if (tokenRef.current) {
			const token = tokenRef.current;
			tokenRef.current = null;
			setTurnstileToken(null);
			setTurnstileReset(n => n + 1);
			return Promise.resolve(token);
		}
		return new Promise((resolve, reject) => {
			const waiter = { resolve: (token) => { clearTimeout(waiter.timer); resolve(token); } };
			waiter.timer = setTimeout(() => {
				waitersRef.current = waitersRef.current.filter(w => w !== waiter);
				const error = new Error('The bot check did not finish.');
				error.response = { status: 400, data: { code: 'challenge_failed' }, headers: {} };
				reject(error);
			}, CHALLENGE_WAIT_MS);
			waitersRef.current.push(waiter);
		});
	}, []);
	useEffect(() => () => {
		waitersRef.current.forEach(waiter => clearTimeout(waiter.timer));
		waitersRef.current = [];
	}, []);

	// ---- the visitor's files (KPR-388) ----

	const writeFiles = useCallback((change) => {
		const next = change(filesRef.current);
		filesRef.current = next;
		setFiles(next);
	}, []);
	const findEntry = uid => Object.values(filesRef.current).flat().find(entry => entry.uid === uid) || null;
	const patchEntry = useCallback((uid, patch) => writeFiles(prev => Object.fromEntries(
		Object.entries(prev).map(([name, list]) => [name, list.map(entry => (entry.uid === uid ? { ...entry, ...patch } : entry))])
	)), [writeFiles]);

	// One file to the link, under `token`. A ref, so the retries and the
	// token refresh below always call the current one.
	const sendRef = useRef(null);
	const resendAllRef = useRef(null);
	sendRef.current = async (uid, token, attempt = 0) => {
		const entry = findEntry(uid);
		if (!entry) return;
		patchEntry(uid, { state: 'uploading', percent: 0, error: null, token });
		try {
			// A permanent public form: a fresh bot-check token for THIS file.
			const turnstileToken = uploadNeedsChallenge && siteKeyRef.current ? await takeChallengeToken() : undefined;
			if (!mountedRef.current || !findEntry(uid)) return;
			const created = await calls.upload(
				{ file: entry.file, element: entry.element, submissionToken: token, turnstileToken },
				{ onProgress: percent => { if (mountedRef.current && findEntry(uid)) patchEntry(uid, { percent }); } }
			);
			if (!mountedRef.current) return;
			// Removed while it was on its way: take it back off the server too.
			if (!findEntry(uid)) {
				if (created?.id) calls.remove(created.id, token).catch(() => {});
				return;
			}
			// The form was reloaded while this file travelled (KPR-515): it is
			// held under a token that can no longer submit. Take it back under
			// that token and send it again under the current one (the
			// independent review's L6).
			const current = viewRef.current?.submissionToken;
			if (current && current !== token) {
				if (created?.id) calls.remove(created.id, token).catch(() => {});
				await sendRef.current(uid, current);
				return;
			}
			patchEntry(uid, { state: 'done', percent: 100, id: created?.id || null, size: Number.isFinite(created?.size) ? created.size : entry.size, token });
		} catch (error) {
			if (!mountedRef.current || !findEntry(uid)) return;
			const status = error?.response?.status;
			const why = error?.response?.data?.code;
			if (status === 404) { onDead?.(); return; }
			if (status === 400 && why === 'form_out_of_date') { onOutOfDate?.(); return; }
			if (why === 'upload_busy' && attempt < BUSY_RETRIES) {
				await sleep((uploadRetryAfter(error) || 3) * 1000);
				await sendRef.current(uid, viewRef.current?.submissionToken || token, attempt + 1);
				return;
			}
			if (why === 'submission_token_invalid' || why === 'form_changed') {
				// The form aged out (submission_token_invalid), or CHANGED
				// (KPR-515: 409 form_changed, a public form only) under the
				// visitor: reload it — a fresh token, the answers kept — and
				// every file on the form goes again under the new token (an
				// upload is bound to the token it was sent with). This file is
				// marked to go too: left `uploading` it would wait for ever.
				// Already reloaded since this file set off: it just goes again.
				const current = viewRef.current?.submissionToken;
				if (current && current !== token) { await sendRef.current(uid, current); return; }
				patchEntry(uid, { state: 'error', error: null, retryable: true });
				const reloaded = await reloadRef.current?.(why === 'form_changed' ? 'changed' : 'expired');
				if (reloaded && mountedRef.current) { setNotice(reloaded.notice); setRestoredDraft(false); }
				return;
			}
			patchEntry(uid, { state: 'error', error: describeUploadError(error), retryable: !FINAL_UPLOAD_CODES.includes(why) });
		}
	};
	resendAllRef.current = (token) => {
		Object.values(filesRef.current).flat()
			.filter(entry => entry.token !== token && (entry.state === 'done' || (entry.state === 'error' && entry.retryable !== false)))
			.forEach(entry => { sendRef.current(entry.uid, token); });
	};

	// ---- the form changed, or aged out, under the visitor (KPR-515) ----
	//
	// One reload at a time (several uploads can meet the change at once):
	// a fresh descriptor and token (refreshToken — which also hands a form
	// that died, paused, filled up or stopped to the page above), then the
	// answers put back by element name (utils/formChanges carryValues: still
	// asked, the same type), the files of file elements still asked sent
	// again under the new token, the rest dropped — and, when the form
	// CHANGED, their old holds taken back under the old token, which still
	// verifies, so nothing waits on the server for the reaper — and the
	// questions that changed marked, with the first scrolled to. Rejected:
	// unmounting the page and opening a fresh one with the values (KPR-513's
	// seam): the photos the visitor picked live in this page, and a new page
	// would have had to ask for them again.
	const reloadRef = useRef(null);
	const reloadingRef = useRef(null);
	// Send waits while the form is being fetched again.
	const [reloading, setReloading] = useState(false);
	reloadRef.current = (reason) => {
		if (reloadingRef.current) return reloadingRef.current;
		const before = Array.isArray(viewRef.current?.elements) ? viewRef.current.elements.filter(def => def?.name) : [];
		setReloading(true);
		const run = (async () => {
			const fresh = await refreshToken();
			if (mountedRef.current) setReloading(false);
			if (!fresh || !mountedRef.current) return null;
			// AN INVITE LINK keeps exactly what it did before KPR-515 (the plan:
			// invite links unchanged): an aged-out token is replaced, every
			// answer stays where it is, every file goes again. Only a permanent
			// public form (`draftKey`) is reconciled and marked below.
			if (!draftKey) {
				const sendingAgain = Object.values(filesRef.current).flat().length > 0;
				if (sendingAgain) resendAllRef.current?.(fresh.submissionToken);
				return {
					fresh,
					notice: sendingAgain
						? 'This form had been open a while, so it was refreshed and your files are being sent again. Your answers are still here — send it when they are ready.'
						: 'This form had been open a while, so it was refreshed. Your answers are still here — send it again.',
				};
			}
			const defs = (Array.isArray(fresh.elements) ? fresh.elements : []).filter(def => def?.name);
			const oldShape = formShape(before);
			const { carried, dropped } = carryValues(defs, valuesRef.current, oldShape);
			const nextValues = withShownValues(defs, { ...initialValuesFor(defs, fresh.timezone), ...carried });

			// The files: kept for a file element still asked (one, when it no
			// longer takes several), dropped — and taken back — otherwise.
			const byName = new Map(defs.map(def => [def.name, def]));
			const kept = {};
			Object.entries(filesRef.current).forEach(([name, list]) => {
				const def = byName.get(name);
				const keep = def && isFileElement(def) ? (def.options?.allowMultiple === true ? list : list.slice(0, 1)) : [];
				list.forEach((entry) => {
					const staying = keep.includes(entry);
					// The old hold goes either way when the form changed: a kept
					// file is sent again under the new token.
					if ((!staying || reason === 'changed') && entry.id && entry.token && entry.token !== fresh.submissionToken) {
						calls.remove(entry.id, entry.token).catch(() => {});
					}
					if (!staying) dropPreview(entry);
				});
				if (keep.length) kept[name] = keep;
			});
			writeFiles(() => kept);
			const sending = Object.values(kept).flat().length > 0;
			if (sending) resendAllRef.current?.(fresh.submissionToken);

			// The questions to point at, judged with the files the visitor
			// still has counted as answers.
			const withFiles = { ...nextValues };
			Object.entries(kept).forEach(([name, list]) => { withFiles[name] = list.map(entry => entry.uid); });
			const found = changedQuestions(defs, oldShape, withFiles, dropped);
			const failing = checkAnswers(defs, withFiles);
			setValues(nextValues);
			setMarks(prev => (Object.keys(found).length
				? found
				: Object.fromEntries(Object.entries(prev).filter(([name]) => byName.has(name)))));
			setErrors(Object.fromEntries(Object.keys(found).filter(name => failing[name]).map(name => [name, failing[name]])));
			setFileErrors({});
			setFocusFirst(Object.keys(found).length > 0);
			// What the visitor is told — said by the CALLER, after its own
			// bookkeeping, so the page never reads as ready while Send is
			// still busy with the request that met the change.
			const lostWords = dropped.length ? ' Answers to questions it no longer asks in the same way were left out.' : '';
			let notice;
			if (reason === 'changed') {
				notice = `This form changed while you were filling it in, so it was reloaded. Your answers are still here.${lostWords}${sending ? ' Your files are being sent again.' : ''} ${Object.keys(found).length
					? 'Look at the highlighted questions, then send it again.'
					: 'Send it again when you are ready.'}`;
			} else if (Object.keys(found).length || dropped.length) {
				notice = `This form had been open a while, so it was refreshed${sending ? ' and your files are being sent again' : ''}. It has changed since: your answers are still here.${lostWords} Look at the highlighted questions, then send it again.`;
			} else {
				notice = sending
					? 'This form had been open a while, so it was refreshed and your files are being sent again. Your answers are still here — send it when they are ready.'
					: 'This form had been open a while, so it was refreshed. Your answers are still here — send it again.';
			}
			return { fresh, notice };
		})();
		reloadingRef.current = run;
		run.finally(() => { reloadingRef.current = null; });
		return run;
	};

	const caps = view?.uploads || {};
	const totals = () => {
		const live = Object.values(filesRef.current).flat().filter(entry => entry.state !== 'error');
		return { count: live.length, bytes: live.reduce((sum, entry) => sum + (Number(entry.size) || 0), 0) };
	};

	const removeEntry = useCallback((name, uid) => {
		const entry = findEntry(uid);
		if (!entry) return;
		if (entry.id && entry.token) calls.remove(entry.id, entry.token).catch(() => {});
		dropPreview(entry);
		writeFiles(prev => ({ ...prev, [name]: (prev[name] || []).filter(e => e.uid !== uid) }));
		setFileErrors(prev => (prev[name] ? { ...prev, [name]: null } : prev));
		setErrors(prev => (prev[name] ? { ...prev, [name]: null } : prev));
	}, [calls, writeFiles]); // eslint-disable-line react-hooks/exhaustive-deps

	const pickFiles = (def, picked) => {
		const name = def.name;
		const multiple = def.options?.allowMultiple === true;
		// A single-file element takes the new file in place of the one it holds.
		const chosen = multiple ? picked : picked.slice(0, 1);
		if (!multiple) (filesRef.current[name] || []).forEach(entry => removeEntry(name, entry.uid));
		let problem = null;
		const added = [];
		for (const file of chosen) {
			const held = totals();
			const count = held.count + added.length;
			const bytes = held.bytes + added.reduce((sum, entry) => sum + entry.size, 0);
			problem = precheckPublicFile(file, def.upload || {}, caps, { count, bytes });
			if (problem) break;
			const kind = publicFileKind(file);
			uidSeq += 1;
			added.push({
				uid: `f${uidSeq}`, element: name, file, name: file.name || (kind === 'pdf' ? 'document.pdf' : 'photo'),
				size: Number(file.size) || 0, kind, previewUrl: previewOf(file, kind), state: 'uploading', percent: 0,
			});
		}
		setFileErrors(prev => ({ ...prev, [name]: problem }));
		setErrors(prev => (prev[name] ? { ...prev, [name]: null } : prev));
		if (!added.length) return;
		writeFiles(prev => ({ ...prev, [name]: [...(prev[name] || []), ...added] }));
		const token = viewRef.current?.submissionToken;
		added.forEach(entry => { sendRef.current(entry.uid, token); });
	};

	const retryEntry = useCallback((uid) => {
		sendRef.current(uid, viewRef.current?.submissionToken);
	}, []);

	// The file elements' values: the ids of the files that are on the server.
	const fileValues = useMemo(() => {
		const out = {};
		elements.filter(isFileElement).forEach((def) => {
			const ids = (files[def.name] || []).filter(entry => entry.state === 'done' && entry.id).map(entry => entry.id);
			if (ids.length) out[def.name] = def.options?.allowMultiple === true ? ids : ids[0];
		});
		return out;
	}, [elements, files]);
	const fileEntries = Object.values(files).flat();
	const sendingFiles = fileEntries.some(entry => entry.state === 'uploading');

	const handleSubmit = useCallback(async (event) => {
		event?.preventDefault?.();
		if (inFlight.current) return;
		setFormError(null);
		setPlanLimit(null);
		setNotice(null);
		setRestoredDraft(false);

		if (fileEntries.some(entry => entry.state === 'uploading')) {
			setFormError('Wait for your files to finish sending.');
			return;
		}
		if (fileEntries.some(entry => entry.state === 'error')) {
			setFormError('Some files were not sent. Try them again, or remove them.');
			return;
		}
		const merged = { ...values, ...fileValues };
		// A read-only element (KPR-519) is the server's to fill: never judged
		// here, never sent.
		// …and a repeating group's entries, each column beside its entry (KPR-689).
		const found = checkAnswers(elements, merged);
		if (Object.keys(found).length) {
			setErrors(found);
			setFormError('Some answers need a look before this can be sent.');
			return;
		}

		// Only what was answered goes up: the API's validator is strict, and
		// an empty spelling is not an answer.
		const answered = {};
		answerableOf(elements).forEach((def) => {
			const value = merged[def.name];
			// A repeating group's entries as the API takes them: new entries,
			// the columns the visitor fills in, never the form's own keys.
			if (isRepeatingGroup(def)) {
				const wire = entriesForWire(def, entriesOf(value), { isNew: true });
				if (Array.isArray(wire) && wire.length) answered[def.name] = wire;
				return;
			}
			if (!isElementValueEmpty(def, value)) answered[def.name] = value;
		});

		inFlight.current = true;
		setSubmitting(true);
		try {
			const result = await calls.submit({
				submissionToken: view?.submissionToken,
				turnstileToken: turnstileToken || undefined,
				elements: answered,
			});
			tokenRef.current = null;
			// Sent: nothing of it stays in the tab (KPR-515's draft).
			dirtyRef.current = false;
			if (draftKey) clearDraft(draftKey);
			setMarks({});
			setDone(result || { ok: true });
		} catch (error) {
			const status = error?.response?.status;
			// Expired, revoked, the cap reached, the form switched off — the
			// API cannot say which, and neither can this page.
			if (status === 404) { onDead?.(); return; }
			// The widget's token is single use; any failed attempt needs a new one.
			tokenRef.current = null;
			setTurnstileReset(n => n + 1);
			setTurnstileToken(null);
			// The owner's plan limit (409 `plan_limit`, KPR-1095): said in place
			// in the PlanLimit pattern — to a visitor only that the form isn't
			// taking submissions right now — and every answer stays.
			const limitRefusal = planLimitOf(error);
			if (limitRefusal) {
				setPlanLimit(limitRefusal);
				return;
			}
			const data = error?.response?.data || {};
			// The form CHANGED under the visitor (KPR-515, 409 form_changed —
			// a permanent public form; the owner's ruling f09), or its token
			// aged out (400 submission_token_invalid). Neither spent anything,
			// so a fresh token is one public GET away: the page reloads the
			// form with the answers kept, points at what changed, and the
			// visitor sends it again.
			if (data.code === 'form_changed' || data.code === 'submission_token_invalid') {
				// This send is over: Send stops spinning now, and waits on the
				// reload (`reloading`) instead.
				inFlight.current = false;
				setSubmitting(false);
				const reloaded = await reloadRef.current?.(data.code === 'form_changed' ? 'changed' : 'expired');
				if (reloaded) { setNotice(reloaded.notice); setRestoredDraft(false); }
				return;
			}
			const refusedGroup = data.element ? elements.find(def => def.name === data.element && isRepeatingGroup(def)) : null;
			if (refusedGroup) {
				// A refusal about a repeating group's entries (KPR-689): each on
				// its entry and column, the group's count sentence on the group.
				const placed = groupRefusalErrors(data, refusedGroup, entriesOf(values[refusedGroup.name]));
				if (placed) setErrors(prev => ({ ...prev, ...placed }));
			} else if (data.element && elements.some(def => def.name === data.element)) {
				// The API names a field's message "elements.<name>: …"; the field says it already.
				const said = typeof data.message === 'string' ? data.message.replace(/^elements\.[^:]+:\s*/, '') : '';
				setErrors(prev => ({ ...prev, [data.element]: said || 'Check this answer.' }));
			}
			// A refusal placed on a group's entries (KPR-689) is said there; up
			// here, in words, as the form's own check says it.
			if (refusedGroup) {
				setFormError('Some answers need a look before this can be sent.');
				return;
			}
			// A form a card change stopped (KPR-515): what they typed waits in
			// this tab (the draft), and they are told so.
			setFormError(data.code === 'form_out_of_date' && draftKey
				? 'This form needs updating by its owner. Your answers are kept in this tab for two hours (not private answers or photos) — open this page again once it is fixed and they will be back.'
				: describeSubmitError(error));
		} finally {
			inFlight.current = false;
			setSubmitting(false);
		}
	}, [calls, elements, values, view, turnstileToken, onDead, fileEntries, fileValues, draftKey]);

	const handleAnother = useCallback(async () => {
		const fresh = await refreshToken();
		if (!fresh) return;
		dirtyRef.current = false;
		setValues(initialValuesFor(fresh.elements, fresh.timezone));
		setErrors({});
		setMarks({});
		// The sent files belong to the record now; their previews go.
		Object.values(filesRef.current).flat().forEach(dropPreview);
		writeFiles(() => ({}));
		setFileErrors({});
		setFormError(null);
		setPlanLimit(null);
		setNotice(null);
		tokenRef.current = null;
		setTurnstileReset(n => n + 1);
		setTurnstileToken(null);
		setDone(null);
	}, [refreshToken, writeFiles]);

	// Throw the restored answers away: the form's own starting values, no
	// marks, and nothing left in the tab.
	const startOver = () => {
		if (draftKey) clearDraft(draftKey);
		dirtyRef.current = false;
		setValues(initialValuesFor(view?.elements, view?.timezone));
		setErrors({});
		setMarks({});
		setNotice(null);
		setRestoredDraft(false);
	};

	// A question the form changed under the visitor (KPR-515): framed, with
	// its change in WORDS above it (never colour alone), and the anchor the
	// scroll finds. Unmarked fields render exactly as they always have, and
	// an invite link's form never has a mark (only a public form's reload or
	// draft makes them).
	const marked = (element, node) => {
		const mark = marks[element.name];
		if (!mark) return node;
		return (
			<div key={element.name} className="shared-form__changed" data-form-change={element.name}>
				<span className="shared-form__changed-tag">{CHANGE_WORDS[mark]}</span>
				{node}
			</div>
		);
	};

	if (done) {
		return (
			<div className="shared-form shared-form--done" data-testid="shared-form-done">
				<Result
					status="success"
					title={done.confirmation || 'Thank you — your answers were sent.'}
					subTitle={`Your answers go to the people who keep ${collection?.name || 'this collection'}, who review each one.`}
					extra={done.allowAnother ? (
						<Button type="primary" onClick={handleAnother}>Submit another</Button>
					) : null}
				/>
			</div>
		);
	}

	return (
		<WithoutCardDefinitions>
			<div className="shared-form">
				<header className="shared-form__header">
					<RecordIdentity
						kicker={collection?.name || null}
						badge={(
							// The form's own surface: the card's chip beside a plain
							// title, never a pill (platform-standards §1).
							<RecordTitle
								kind="card"
								record={card}
								title={view?.headline || cardTitle}
								size="xl"
								wrap
								titleAs="h1"
							/>
						)}
						below={(
							<>
								{view?.headline ? <span>{cardTitle}</span> : null}
								{expires ? <span className="shared-form__expiry">Open until {expires}</span> : null}
							</>
						)}
					/>
				</header>

				{view?.intro ? <p className="shared-form__intro">{view.intro}</p> : null}
				{!view?.intro && card?.description ? <p className="shared-form__intro">{card.description}</p> : null}

				{published ? (
					<p className="shared-form__note" data-testid="shared-form-published-note">
						This collection is public. The people who keep it review each answer first; once they accept
						it, it is visible to everyone who uses keepr
						{privateElements.length ? ' — except your answers under “Only for the people who run this collection”, which are never shown with it' : ''}.
						This form shows you nothing else from the collection.
					</p>
				) : (
					<p className="shared-form__note">
						Your answers go to the people who keep this collection, who review each one. Nothing
						you send is published, and this form shows you nothing else from the collection.
					</p>
				)}

				{notice ? (
					<Alert
						className="shared-form__alert"
						type="info"
						showIcon
						message={notice}
						action={restoredDraft ? <Button size="small" onClick={startOver}>Start over</Button> : undefined}
					/>
				) : null}
				{formError ? <Alert className="shared-form__alert" type="error" showIcon message={formError} /> : null}
				<PlanLimitAlert refusal={planLimit} className="shared-form__alert" />

				<form className="shared-form__form" onSubmit={handleSubmit} noValidate>
					<div className="shared-form__fields">
						{publicElements.map(element => marked(element, isReadOnlyElement(element) ? (
							// Shown, not asked (KPR-519): the value the record will
							// hold, disabled, and the one sentence that says why.
							<div key={element.name} className="shared-form__shown" data-testid={`shared-form-shown-${element.name}`}>
								<EventFormElement
									event={{ elements: values }}
									element={element}
									value={values[element.name]}
									onChange={handleChange}
									siblingDefs={elements}
									showIconBadge={false}
									disabled
								/>
								<p className="shared-form__shown-note">
									<LOCK className="shared-form__shown-icon" aria-hidden="true" />
									<span>Set by the people who keep this collection. You can&rsquo;t change it.</span>
								</p>
							</div>
						) : isFileElement(element) ? (
							<PublicFileField
								key={element.name}
								element={element}
								entries={files[element.name] || []}
								onPick={picked => pickFiles(element, picked)}
								onRemove={uid => removeEntry(element.name, uid)}
								onRetry={retryEntry}
								errorMessage={errors[element.name] || fileErrors[element.name] || null}
								full={Boolean(caps.maxFiles) && fileEntries.filter(entry => entry.state !== 'error').length >= caps.maxFiles}
								disabled={submitting}
							/>
						) : (
							<EventFormElement
								key={element.name}
								event={{ elements: values }}
								element={element}
								value={values[element.name]}
								onChange={handleChange}
								errorMessage={errors[element.name] || null}
								siblingDefs={elements}
								showIconBadge={false}
								// A repeating group (KPR-689): the messages filed under
								// its entries, and the zone a new entry's defaults read.
								entryErrors={isRepeatingGroup(element) ? errors : null}
								zone={isRepeatingGroup(element) ? collectionZone(view?.timezone) : undefined}
								disabled={isRepeatingGroup(element) ? submitting : undefined}
							/>
						)))}
					</div>

					{privateElements.length ? (
						<section className="shared-form__private" aria-labelledby="shared-form-private-title" data-testid="shared-form-private">
							<h2 className="shared-form__private-title" id="shared-form-private-title">
								Only for the people who run this collection
							</h2>
							<p className="shared-form__private-hint">
								These answers are kept apart from the rest of what you send. They are never part of the
								record{published ? ', never shown to the public' : ''}, and they are deleted if your
								submission is turned down.
							</p>
							<div className="shared-form__fields">
								{privateElements.map(element => marked(element, (
									<EventFormElement
										key={element.name}
										event={{ elements: values }}
										element={element}
										value={values[element.name]}
										onChange={handleChange}
										errorMessage={errors[element.name] || null}
										siblingDefs={elements}
										showIconBadge={false}
									/>
								)))}
							</div>
						</section>
					) : null}

					{needsChallenge && siteKey ? (
						<TurnstileWidget
							siteKey={siteKey}
							action="intake"
							resetKey={turnstileReset}
							onToken={handleWidgetToken}
						/>
					) : null}
					{needsChallenge && !siteKey ? (
						<Alert
							className="shared-form__alert"
							type="warning"
							showIcon
							message="This form cannot run its bot check here, so it cannot be sent right now. Tell whoever shared it."
						/>
					) : null}

					<section className="shared-form__legal" aria-labelledby="shared-form-legal-title" data-testid="shared-form-legal">
						<h2 className="shared-form__legal-title" id="shared-form-legal-title">{FORM_NOTICE_HEADING}</h2>
						{(published ? FORM_NOTICE_PUBLISHED : FORM_NOTICE_PRIVATE).map(line => <p key={line}>{line}</p>)}
					</section>

					<div className="shared-form__actions">
						<Button
							type="primary"
							htmlType="submit"
							loading={submitting}
							disabled={reloading || sendingFiles || (needsChallenge && (!siteKey || !turnstileToken))}
						>
							{submitting ? 'Sending' : 'Send'}
						</Button>
						{sendingFiles ? <span className="shared-form__hint">Sending your files…</span> : null}
						{!sendingFiles && needsChallenge && siteKey && !turnstileToken ? (
							<span className="shared-form__hint">Checking you are not a bot…</span>
						) : null}
					</div>
				</form>

				{/* G16 (PR 10c): a public form can be reported like any public page —
				    an invite link by its code, a permanent public form by the link
				    its page hands in (KPR-514). */}
				{reportLink || code ? (
					<footer className="shared-view__report">
						{reportLink || <ReportPageLink code={code} />}
					</footer>
				) : null}
			</div>
		</WithoutCardDefinitions>
	);
}
